// Filter bar for the Unified Feedback Inbox (docs/06-ux-rules.md "Filter
// bar"; 11-chassis-plan.md §2 PR 03). Three zones in one row: Status (left)
// · Kind + optional 👍/👎 (centre) · Location (right). Presentational —
// `FeedbackInbox/index.tsx` owns the URL params, the counts/locations
// queries, and calls `setFilter` on every change.
//
// aria-pressed on uikit Button (11-chassis-plan.md §4 "Open items"):
// verified directly against `tsc` — `Button`'s prop type accepts
// `aria-pressed`/`aria-label` structurally (it spreads unknown props
// through to baseui's Button, which forwards them to the root `<button>`).
//
// Segments have NO visible hover tooltip (confirmed against design,
// 2026-09-18) — the pin #1153 copy below (`status_tooltip`/`kind_tooltip`)
// is an accessible description only, surfaced via `aria-label`.
//
// Segment visuals are NOT `ButtonGroup`/`ToggleButton` + a `className`
// override (deviation from the plan's literal wording, found by
// screenshotting a first draft): `ButtonGroup` hardcodes a 60px per-button
// width via `cloneElement` (button-group/ButtonGroup.js) that mangled our
// longer, count-suffixed labels ("RecoveryAI Escalations (4)") into
// overlapping multi-line text. Dropping `ButtonGroup` alone wasn't enough
// either — a `className` passed to `ToggleButton`/`Button` competes with
// baseui's OWN internal `overrides.BaseButton.style` function for the same
// CSS properties (both atomic-class systems, tie-broken by stylesheet
// insertion order, not JSX/class-attribute order), and baseui's own
// padding/sizing rules kept winning that race — confirmed by dumping the
// live DOM (`chrome --dump-dom`) and finding baseui's ~50 internal atomic
// classes inserted after ours regardless of source order. The reliable fix
// is `overrides.BaseButton.style` itself: providing our OWN style function
// there REPLACES @thanx/uikit's (it spreads the consumer's overrides after
// its own — `Button.js`: `...(overrides || {})` — a shallow merge at the
// slot level, not a deep one). That ends the race for the properties we
// declare, but it also means every OTHER property uikit's `kind` generator
// would have supplied is GONE, falling through to baseui's own base style —
// and since uikit never forwards `kind` to baseui, that base style is
// baseui's default `$kind = primary`, not `minimal`. So an undeclared state
// gets a PRIMARY treatment (e.g. `:hover` → near-black
// `buttonPrimaryHover`), which is why `segmentButtonStyle`
// below returns a full style object keyed on `active`, applied via
// `overrides` on a plain uikit `Button` (no `ToggleButton` `kind` remap
// needed once colors are hand-specified).
import { Button } from '@thanx/uikit/button'
import type {
  FeedbackInboxCounts,
  InboxKind,
  InboxLocation,
  InboxStatus,
  RatingFilter,
  SetupState,
} from 'api/feedbackInbox.types'
import CountBadge from 'components/CountBadge'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { RESOLVED_PULSE_DURATION_MS } from '../constants'
import LocationFilter from '../LocationFilter'
import type { InboxFilterPatch } from '../useInboxParams'

const t = buildTranslate('feedback_inbox.filter_bar')

const STATUS_SEGMENTS: InboxStatus[] = ['all', 'open', 'resolved']

// Kind segments by setup state (docs/06-ux-rules.md "Kind segments by setup
// state" / FR-64): RAI `all` hides the Order Feedback segment entirely,
// never just disables it. Written comments off (FR-23) hides it the same way:
// the segment lists only commented thumbs-downs, so it would always be empty.
export function kindSegmentsFor(
  setupState: SetupState,
  writtenFeedbackEnabled = true
): InboxKind[] {
  switch (setupState) {
    case 'off':
      return writtenFeedbackEnabled ? ['feedback', 'all'] : ['all']
    case 'all':
      return ['recovery_ai', 'all']
    case 'pilot':
    default:
      return writtenFeedbackEnabled
        ? ['feedback', 'recovery_ai', 'all']
        : ['recovery_ai', 'all']
  }
}

// No count on "All" (docs/06-ux-rules.md "Counts").
function countFor(
  segment: InboxKind,
  counts: FeedbackInboxCounts | undefined
): number | null {
  if (segment === 'all' || !counts) return null
  return segment === 'feedback' ? counts.feedback : counts.recoveryAi
}

// Pill tone per kind segment (v83 `.f-topbar .src-n`): grey while the
// segment is inactive; once active, amber for Order Feedback and teal for
// RecoveryAI Escalations. Never called for "all" — that segment never has a
// count, so its `CountBadge` never renders.
export function kindPillTone(
  segment: InboxKind,
  active: boolean
): 'warning' | 'neutral' | 'recoveryAi' {
  if (!active) return 'neutral'
  return segment === 'feedback' ? 'warning' : 'recoveryAi'
}

// Prototype's exact `.f-seg-b.on` shadow (v83). A shadow's alpha tint isn't
// expressible via a solid `palette` token — same precedent as
// InboxLayout.tsx's INBOX_CARD_SHADOW.
const SEGMENT_ACTIVE_SHADOW = '0 1px 2px rgba(30, 38, 48, 0.12)'
// Prototype's exact `.src-b.on` shadow (v83) for the kind segments.
const KIND_SEGMENT_ACTIVE_SHADOW = '0 1px 3px rgba(0, 0, 0, 0.12)'

// The two segment groups differ in v83: status `.f-seg-b` (belize active
// label, weight 700, 6px padding/radius) and kind `.src-b` (grey90 active
// label, weight 800, 7px padding/radius, its own shadow).
type SegmentVariant = 'status' | 'kind'
const SEGMENT_VARIANT_STYLE = {
  status: {
    activeColor: palette.belizeHole,
    fontWeight: 700,
    paddingY: '6px',
    radius: '6px',
    activeShadow: SEGMENT_ACTIVE_SHADOW,
  },
  kind: {
    activeColor: palette.grey90,
    fontWeight: 800,
    paddingY: '7px',
    radius: '7px',
    activeShadow: KIND_SEGMENT_ACTIVE_SHADOW,
  },
}

// 2px belize ring for keyboard focus (WCAG 2.4.7) — see segmentButtonOverrides.
const FOCUS_RING_SHADOW = `0 0 0 2px ${palette.belizeHole}`

// baseui drives its own style fn (and focus ring) off `$isFocusVisible`, not
// a `:focus-visible` CSS pseudo-class — see styled-components.js on
// `BaseButton`. Kept as its own function so the active-shadow/focus-ring
// composition (comma-joined, not one replacing the other) is exercised
// directly in unit tests.
type BaseButtonStyleProps = { $isFocusVisible: boolean }

function segmentBoxShadow(
  active: boolean,
  isFocusVisible: boolean,
  activeShadow: string
): string {
  if (!isFocusVisible) return active ? activeShadow : 'none'
  return active ? `${activeShadow}, ${FOCUS_RING_SHADOW}` : FOCUS_RING_SHADOW
}

// See the file header comment — this fully replaces baseui's own
// `overrides.BaseButton.style`, so every visual property a segment button
// needs (sizing, color, hover) is specified here rather than relying on the
// uikit `Button`'s `kind` prop.
//
// Segments render as a single joined pill, not individually outlined chips
// (v69 prototype `.f-seg`/`.f-seg-b` at index.html:1103-1105): the button
// itself is borderless here, and `segmentRowStyle` below supplies the outer
// background/border/radius/padding that makes the group read as one
// control. The active segment becomes a white pill with a drop shadow
// rather than a solid-color chip.
//
// baseui's focus ring lives in the style fn we replace — re-supplied here (a11y).
// Exported so the focus-ring composition is unit-testable (see index.test.tsx) —
// styletron's css() is a no-op in the default jsdom test env, so the value passed
// to `overrides` is the only thing a style-object-only suite can assert on.
export function segmentButtonOverrides(
  active: boolean,
  variant: SegmentVariant = 'status'
) {
  const v = SEGMENT_VARIANT_STYLE[variant]
  return {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        whiteSpace: 'nowrap' as const,
        width: 'auto',
        height: 'auto',
        lineHeight: '18px',
        // Longhand, not shorthand `padding: '6px 12px'` — baseui's own
        // BaseButton sizes itself via longhand paddingTop/Bottom/Left/Right
        // (button/styled-components.js `getSizeStyle`), merged into this
        // style object key-by-key rather than replaced wholesale (same class
        // of bug as InboxListRow.tsx's BaseButton override, and documented
        // for this same interaction in InboxFilterBar's own file header
        // comment above). Confirmed live: with the shorthand, vertical
        // padding leaked baseui's default-size 14px instead of this 6px —
        // horizontal came through fine, only top/bottom were affected — and
        // the whole filter bar read visibly thicker than designed.
        paddingTop: v.paddingY,
        paddingBottom: v.paddingY,
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: '13px',
        fontWeight: v.fontWeight,
        border: 'none',
        borderTopLeftRadius: v.radius,
        borderTopRightRadius: v.radius,
        borderBottomRightRadius: v.radius,
        borderBottomLeftRadius: v.radius,
        backgroundColor: active ? palette.white : 'transparent',
        color: active ? v.activeColor : palette.grey70,
        boxShadow: segmentBoxShadow(active, $isFocusVisible, v.activeShadow),
        ':hover': {
          backgroundColor: active ? palette.white : palette.grey10,
        },
      }),
    },
  }
}

// 👍/👎 toggle (docs/06-ux-rules.md "👍/👎 toggle"): pixel-sampled against
// the Sketch reference (v82) via a real signed-in browser session — this
// control is visually its OWN thing, not a third `segmentRowStyle` pill.
// The container is a DASHED outline (not the solid grey05-filled pill the
// status/kind segments use), each icon is borderless/transparent until
// active, and the two icons are separated by a plain "/" glyph rather than
// sitting in a shared joined background. Sampled colors matched existing
// tokens exactly: inactive stroke = grey50, divider/border = grey40, active
// thumbs-up = nephritis stroke on a nephritis10 tile, active thumbs-down =
// cayenne stroke on a cayenne05 tile (this codebase's nearest red token —
// see palette.ts's own TUTD comment on cayenette-as-red).
// Active-tile color per rating — the icon's own stroke color is set
// directly in the JSX (it isn't a baseui style-fn concern), so this only
// needs to expose the pale background tile.
function ratingActiveTileFor(rating: 'up' | 'down'): string {
  return rating === 'up' ? palette.nephritis10 : palette.cayenne05
}

export function ratingButtonOverrides(rating: 'up' | 'down', active: boolean) {
  const tile = ratingActiveTileFor(rating)
  return {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        width: 'auto',
        height: 'auto',
        lineHeight: 1,
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingLeft: '6px',
        paddingRight: '6px',
        border: 'none',
        borderTopLeftRadius: '6px',
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        borderBottomLeftRadius: '6px',
        backgroundColor: active ? tile : 'transparent',
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        ':hover': {
          backgroundColor: active ? tile : palette.grey10,
        },
      }),
    },
  }
}

export interface InboxFilterBarProps {
  status: InboxStatus
  kind: InboxKind
  rating: RatingFilter | null
  locationIds: number[]
  setupState: SetupState
  // Merchant's written-feedback collection setting (FR-23); false hides the
  // Order Feedback segment. Defaults to true.
  writtenFeedbackEnabled?: boolean
  counts?: FeedbackInboxCounts
  locations: InboxLocation[]
  onFilterChange: (patch: InboxFilterPatch) => void
}

// Imperative escape hatch for the resolve animation (docs/06-ux-rules.md
// "Resolve animation" — "+1 pops... on the Resolved tab"; 11-chassis-
// plan.md §2 PR 04: "InboxFilterBar exposes pulseResolvedCount() via ref").
// A ref (not a prop/event bus) because the trigger is a one-shot imperative
// animation cue from the container, not state this component itself owns
// or needs to re-render for on every call.
export interface InboxFilterBarHandle {
  pulseResolvedCount: () => void
}

const InboxFilterBar = React.forwardRef<
  InboxFilterBarHandle,
  InboxFilterBarProps
>(function InboxFilterBar(
  {
    status,
    kind,
    rating,
    locationIds,
    setupState,
    writtenFeedbackEnabled = true,
    counts,
    locations,
    onFilterChange,
  },
  ref
) {
  const [css] = useStyletron()
  const [pulseToken, setPulseToken] = useState(0)
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useImperativeHandle(
    ref,
    () => ({
      pulseResolvedCount: () => setPulseToken(token => token + 1),
    }),
    []
  )

  useEffect(() => {
    if (pulseToken === 0) return undefined
    pulseTimeoutRef.current = setTimeout(
      () => setPulseToken(0),
      RESOLVED_PULSE_DURATION_MS
    )
    return () => {
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current)
    }
  }, [pulseToken])

  // Outer "pill" container for a segmented group (v69 prototype `.f-seg`,
  // index.html:1103) — background/border/radius/padding live here so the
  // group reads as one joined control; each `Button` inside is borderless
  // (see `segmentButtonOverrides`).
  const segmentRowStyle = css({
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: palette.grey05,
    border: `1px solid ${palette.grey20}`,
    borderRadius: '8px',
    padding: '3px',
  })

  const kindSegments = kindSegmentsFor(setupState, writtenFeedbackEnabled)

  return (
    <div
      className={css({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        width: '100%',
      })}
    >
      <div
        role="group"
        aria-label={t('status_group_aria')}
        className={segmentRowStyle}
      >
        {STATUS_SEGMENTS.map(segment => {
          const tooltipText = t(`status_tooltip.${segment}`)
          return (
            <span
              key={segment}
              className={css({ position: 'relative', display: 'inline-flex' })}
            >
              <Button
                onClick={() => onFilterChange({ status: segment })}
                aria-label={tooltipText}
                aria-pressed={status === segment}
                overrides={segmentButtonOverrides(status === segment)}
              >
                {t(`status.${segment}`)}
              </Button>
              {segment === 'resolved' && pulseToken > 0 && (
                <span
                  key={pulseToken}
                  aria-hidden="true"
                  className={css({
                    position: 'absolute',
                    top: '-9px',
                    right: '-12px',
                    backgroundColor: palette.nephritis,
                    color: palette.white,
                    fontSize: '11px',
                    fontWeight: 900,
                    lineHeight: 1,
                    padding: '3px 6px',
                    borderRadius: '10px',
                    whiteSpace: 'nowrap',
                    zIndex: 5,
                    animationName: {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(5px) scale(0.8)',
                      },
                      '22%': {
                        opacity: 1,
                        transform: 'translateY(-1px) scale(1.08)',
                      },
                      '34%': {
                        opacity: 1,
                        transform: 'translateY(-2px) scale(1)',
                      },
                      '68%': {
                        opacity: 1,
                        transform: 'translateY(-2px) scale(1)',
                      },
                      '100%': {
                        opacity: 0,
                        transform: 'translateY(-9px) scale(0.95)',
                      },
                    } as unknown as string,
                    animationDuration: `${RESOLVED_PULSE_DURATION_MS}ms`,
                    animationTimingFunction: 'ease',
                    animationFillMode: 'forwards',
                  })}
                >
                  {t('resolved_pulse')}
                </span>
              )}
            </span>
          )
        })}
      </div>

      <div
        className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}
      >
        <div
          role="group"
          aria-label={t('kind_group_aria')}
          className={segmentRowStyle}
        >
          {kindSegments.map(segment => {
            const count = countFor(segment, counts)
            const label = t(`kind.${segment}`)
            const tooltipText = t(`kind_tooltip.${segment}`)
            return (
              <Button
                key={segment}
                onClick={() => onFilterChange({ kind: segment })}
                aria-label={tooltipText}
                aria-pressed={kind === segment}
                overrides={segmentButtonOverrides(kind === segment, 'kind')}
              >
                <span
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  })}
                >
                  {label}
                  {count !== null && (
                    // srLabel is inert here — see kind_count_srlabel's
                    // comment in locales/feedbackInbox.ts: the Button's own
                    // aria-label (pin #1153 copy, unchanged above) fully
                    // determines the accessible name and pre-empts any
                    // subtree text, so the count can't double-announce
                    // against it. Visual-only pill for sighted users.
                    <CountBadge
                      count={count}
                      tone={kindPillTone(segment, kind === segment)}
                      srLabel={t('kind_count_srlabel', { label, count })}
                    />
                  )}
                </span>
              </Button>
            )
          })}
        </div>

        {/* 👍/👎 shown only when Kind = All (docs/06-ux-rules.md "👍/👎
            toggle"); `useInboxParams.setFilter` already clears `rating` when
            the kind changes away from "all" (PR 01), so hiding this control
            here never leaves a stale param behind. */}
        {kind === 'all' && (
          <div
            role="group"
            aria-label={t('rating_group_aria')}
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              border: `1px dashed ${palette.grey40}`,
              borderRadius: '8px',
              padding: '3px 8px',
            })}
          >
            <Button
              onClick={() =>
                onFilterChange({ rating: rating === 'up' ? null : 'up' })
              }
              aria-label={t('rating_up_aria')}
              aria-pressed={rating === 'up'}
              overrides={ratingButtonOverrides('up', rating === 'up')}
            >
              <i
                className="fa fa-thumbs-o-up"
                aria-hidden="true"
                style={{
                  fontSize: '16px',
                  color: rating === 'up' ? palette.nephritis : palette.grey50,
                }}
              />
            </Button>
            <span
              aria-hidden="true"
              className={css({
                color: palette.grey40,
                fontSize: '14px',
                padding: '0 4px',
              })}
            >
              /
            </span>
            <Button
              onClick={() =>
                onFilterChange({ rating: rating === 'down' ? null : 'down' })
              }
              aria-label={t('rating_down_aria')}
              aria-pressed={rating === 'down'}
              overrides={ratingButtonOverrides('down', rating === 'down')}
            >
              <i
                className="fa fa-thumbs-o-down"
                aria-hidden="true"
                style={{
                  fontSize: '16px',
                  color: rating === 'down' ? palette.cayenne : palette.grey50,
                }}
              />
            </Button>
          </div>
        )}
      </div>

      <LocationFilter
        locations={locations}
        selectedIds={locationIds}
        onChange={ids => onFilterChange({ locationIds: ids })}
      />
    </div>
  )
})

export default InboxFilterBar
